import Alert from '@alert';
import { DateFNS, React, Styled } from '@common/3rd';
import { Utils, Envs, Apis, Consts } from '@common';
import { DeskComponentRegistry } from '@component';
import { AjaxResponse, DeskFilePickerComponents, DeskFilePickerProps, DeskFilePickerState, DeskImagePickerProps, StyledPropsTheme } from '@my-types';
import { DeskText } from './desk-text';
import { OssKitHelper as OssKit2 } from '@common/oss-kit2';
import { StyledComponents } from '@styles';

class DeskFilePicker<
	P extends DeskFilePickerProps,
	S extends DeskFilePickerState,
	C extends DeskFilePickerComponents
	> extends DeskText<P, S, C> {
	componentDidMount() {
		if (this.getValueFromModel()) {
			if (this.props.oss) {
				this.setState({
					ossFileName: this.getValueFromModel()
				});
			}
		}
	}
	protected initComponents(): C {
		const C = super.initComponents();
		return Object.assign(C, {
			Box: Styled(C.Box).attrs({
				'data-widget': 'desk-filepicker'
			})`
            flex-wrap: wrap;
			position: relative;
			[data-widget=fas] span {
				left: 0;
			}
		`,
			Text: Styled(C.Text)`
			opacity: 0;
			cursor: pointer;
		`,
			PlaceHolder: Styled.span.attrs({
				'data-widget': 'desk-filepikcer-placeholder'
			})`
			display: block;
			position: absolute;
			cursor: pointer;
			left: 0;
			top: 0;
			height: 100%;
			// z-index: -1;
			color: ${props => props.theme.COLOR_PRIMARY};
			border: 1px solid ${props => props.theme.COLOR_PRIMARY};
			border-radius: ${props => props.theme.BORDER_RADIUS}px;
			line-height: ${props => props.theme.INPUT_LINE_HEIGHT}px;
			height: ${props => props.theme.INPUT_LINE_HEIGHT}px;
			padding-left: 28px;
			padding-right: 28px;
			${props => this.paintColor(props.theme)}
        `,
			OssPlaceHolder: Styled.div.attrs({
				'data-widget': 'desk-filepikcer-oss-placeholder'
			})`
			width: 100%;
			color: ${props => props.theme.BORDER_COLOR};
			> span {
				border-bottom: ${props => props.theme.BORDER};
				cursor: pointer;
				&.no-link {
					border: 0;
				}
			}
        `,
			OssDesc: Styled.div`
            position: absolute;
            left: 130px;
        `
		}) as C;
	}
	protected renderPlaceHolder(): JSX.Element {
		let fileName = this.state.fileName || '立即上传';
		const C = this.getComponents();
		return <C.PlaceHolder>{fileName}</C.PlaceHolder>;
	}
	protected renderOssPlaceHolder(): JSX.Element | null {
		let fileName = this.getValueFromModel();
		const C = this.getComponents();
		let placeholder = this.props.placeholder ? <C.OssPlaceHolder>{this.props.placeholder}</C.OssPlaceHolder> : null;
		let fileNamePlaceholder = fileName ? <C.OssPlaceHolder onClick={() => { this.downloadFile(fileName) }}>
			<StyledComponents.FAS icon="paperclip" />
			<span>查看资源文件</span>
		</C.OssPlaceHolder> : null;
		return <>{placeholder}{fileNamePlaceholder}</>
	}
	private downloadFile = (fileName: string = '') => {
		if (Utils.isSTSKey(fileName)) {
			(async () => {
				const ossFileUrl = await OssKit2.getInstance().signatureUrl(fileName, {
					expires: Consts.STS_SIGNATURE_EXPIRES,
				});
				window.open(ossFileUrl, '_blank');
			})();
		} else if (fileName && fileName.includes('.com/')) {
			let url = fileName.split(/.com\//).pop() + '';
			this.getHelpers().getAjax().get(Apis.STS_ASK_URL.replace(':url', url)).then(resp => {
				if (resp?.body?.body?.linkUrl) {
					window.open(resp?.body?.body?.linkUrl, '_blank');
				}
			})
		}
	}
	protected paintColor(theme: StyledPropsTheme): string {
		return this.isCheckFailOnWhatever() ? `color: ${theme.COLOR_ERROR};margin-bottom:30px;border-color:${theme.COLOR_ERROR};` : '';
	}
	render() {
		const C = this.getComponents();
		return (
			<C.Box>
				{this.renderPlaceHolder()}
				{this.props.oss && this.props.desc ? <C.OssDesc>{this.props.desc}</C.OssDesc> : null}
				<C.Text
					type="file"
					onChange={this.onTextChanged}
					onFocus={this.onTextFocus}
					onBlur={this.onTextBlur}
					accept={this.props.accept}
					ref={this.textRef}
					disabled={!this.isEditable()}
				/>
				{this.renderReminder()}
				{this.renderOssPlaceHolder()}
			</C.Box>
		);
	}
	protected setValueToDOM(value: any, propName?: string): this {
		return this;
	}

	protected getFileDirectory(): string {
		return (this.props.fileDirectory || 'desk-file') as string;
	}
	protected onTextChanged = async (): Promise<void> => {
		const value = this.textRef.current.files[0];
		if (this.props.oss) {
			try {
				if (this.props.accept) {
					//@ts-ignore
					let fileTypeList = this.props.accept.replace(/\./g, '').split(',');
					await Utils.checkFileType(value, fileTypeList);
				}
				//注册页面有个上传组件调用，过滤该状态
				if (Envs.isAuthorized()) {
					Alert.ing('上传中');
					const fileName = `${Consts.STS_FOLDER}/${this.getFileDirectory()}/${DateFNS.format(new Date(), Consts.W3C_DATE_FORMAT)}/${Utils.generateUUID()}.${Utils.generateFileType(value.type)}`;
					// let url = await OssKit.getInstance().uploadFile(value, fileName);
					this.setState({
						ossFileName: fileName
					});
					this.setValueToModel(fileName).checkData();
					Alert.hide();
				} else {
					this.setState({
						ossFileName: this.textRef.current.value
					});
					this.setValueToModel(value).checkData();
				}
			} catch (error) {
				//@ts-ignore
				Alert.info(error || '上传失败, 请稍后再试');
				console.log(`failed to upload file, error message ${error}`);
			} finally {
				this.textRef.current.value = '';
			}
		} else {
			this.setState({
				fileName: this.textRef.current.value
			});
			this.setValueToModel(value).checkData();
		}
	};
	focus(): this {
		this.textRef.current.focus();
		return this;
	}
}

class DeskImagePicker<
	P extends DeskImagePickerProps,
	S extends DeskFilePickerState,
	C extends DeskFilePickerComponents
	> extends DeskFilePicker<P, S, C> {
	protected initComponents(): C {
		const C = super.initComponents();
		return Object.assign(C, {
			Box: Styled(C.Box).attrs({
				'data-widget': 'desk-imagepicker'
			})`
			position: relative;
			flex-direction: column;
			margin-bottom: ${props => props.theme.CELL_BOTTOM_BORDER_GAP + 1}px;
			> img {
				width: 100%;
			}
		`,
			Text: Styled(C.Text)`
			opacity: 0;
			cursor: pointer;
		`,
			PlaceHolder: Styled.span.attrs({
				'data-widget': 'desk-imagepikcer-placeholder'
			})`
			display: block;
			position: absolute;
			cursor: pointer;
			left: 0;
			top: 0;
			height: 100%;
			z-index: -1;
			${props => this.paintColor(props.theme)}
		`,
		}) as C;
	}
	renderImage(): JSX.Element | void {
		const value = this.getValueFromModel();
		if (value != null) {
			return <img src={value} alt="" />;
		}
	}
	render() {
		const C = this.getComponents();
		return (
			<C.Box>
				{this.renderPlaceHolder()}
				<C.Text
					type="file"
					onChange={this.onTextChanged}
					onFocus={this.onTextFocus}
					onBlur={this.onTextBlur}
					accept="image/*"
					ref={this.textRef}
					disabled={!this.isEditable()}
				/>
				{this.renderReminder()}
				{this.renderImage()}
			</C.Box>
		);
	}
	protected onTextChanged = async (): Promise<void> => {
		const image = this.textRef.current.files[0];

		const reader = new FileReader();
		reader.onload = (evt: ProgressEvent) => {
			const formData = new FormData();
			formData.append('file', image);
			this.getHelpers()
				.getAjax()
				.post(this.getUploadUrl(), formData, {
					loading: true,
					headers: {
						'Content-Type': false
					}
				})
				.then((response: AjaxResponse) => {
					this.setValueToModel(((response.body || {}).body || {}).fileName);
					this.forceUpdate();
				})
				.catch(() => {
					Alert.info('上传失败, 请重新选择文件.');
				});
		};
		reader.readAsDataURL(image);
	};
	protected getUploadUrl(): string {
		return this.props.url;
	}
}

DeskComponentRegistry.register({
	filePicker: DeskFilePicker,
	imagePicker: DeskImagePicker
});

export { DeskFilePicker, DeskImagePicker };

